<template>
  <v-expansion-panel>
    <v-expansion-panel-header class="text-systock-blue font-weight-medium font-14">
      <p class="ma-0 mx-n4">Definir proporção por Segmento</p>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="mx-n4">
      <p class="ma-0">
        Defina a participação dos Segmentos no carrinho ajustado
      </p>

      <SysSementosAutocomplete
        v-model="props.parametros.segmentos"
        :fornecedores="fornecedores"
        :unidade_medida="props.parametros.unidade_medida.id"
        :descricao_fornecedor="descricao_fornecedor"
      />

      <table id="tabela">
        <tr>
          <th></th>
          <th class="font-weight-regular">Percentual <br> Desejado</th>
          <th class="font-weight-regular px-1">Valor <br> Ajustado</th>
          <th class="font-weight-regular">Percentual <br> Ajustado</th>
          <th></th>
        </tr>

        <tr v-for="(item, key) in props.parametros.segmentos" :key="key">
          <td>
            <div class="font-weight-medium pl-3">{{ item.descricao }}</div>
          </td>

          <td class="w-100">
            <div class="pr-5">
              <TextFieldFormatadorDeValor
                v-model="item.percentual_ajustado"
                hide-details
                dense
                suffix="%"
                class="ma-0 text-field-centralizado font-14"
                @focus="ev => ev.target.select()"
                @keyup="() => ajustarPercentualProximosSegmentos(item)"
              />
            </div>
          </td>

          <td class="font-weight-medium">
            <div class="px-1 text-no-wrap">
              R$ {{ Helpers.formatarValorComCasas(totalPorSegmento(item.id), 0) }}
            </div>
          </td>

          <td class="font-weight-medium">
            <div>
              <template v-if="totais_produtos_computed.segmentos[item.id]">
                {{ Helpers.formatarValorComCasas(totais_produtos_computed.segmentos[item.id] / totais_produtos_computed.total * 100, 1) }}%
              </template>

              <template v-else>
                0%
              </template>
            </div>
          </td>

          <td>
            <div class="pr-2">
              <v-btn icon x-small @click="item.percentual_ajustado = 0; removerSegmento(item)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </td>
        </tr>
      </table>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script setup>
import TextFieldFormatadorDeValor from "@/components/utils/TextFieldFormatadorDeValor.vue";
import {computed, watch} from "vue";
import SysSementosAutocomplete from "@/components/compra/ProdutosGerais/Carrinho/Meta/Ajustar/SysSementosAutocomplete.vue";

const props = defineProps({
  parametros: Object,
  produtos: Array,
  fornecedores: {
    type: Array,
    default: () => [],
  },
  descricao_fornecedor: {
    type: String,
    required: true
  },
});

const segmentos_computed = computed(() => {
  return Array.from(
    new Map(
      props.produtos
        .filter(item => {
          if (props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA' && props.parametros.unidade_medida.id !== null) {
            return String(item.idunidade_medida || '').toUpperCase() === String(props.parametros.unidade_medida.id || '').toUpperCase();
          }

          return true;
        })
        .map(p => [
          p.iddepartamento,
          {
            id: p.iddepartamento,
            descricao: p.descricao_departamento,
          }
        ])
    ).values()
  );
});

watch(segmentos_computed, () => {
  props.parametros.segmentos = segmentos_computed.value.map(item => {
    const segmento = props.parametros.segmentos.find(segmento => segmento.id === item.id);

    return {
      ...item,
      percentual_ajustado: segmento ? segmento.percentual_ajustado : 0
    };
  });
});

function ajustarPercentualProximosSegmentos(segmento) {
  const total_percentual_configurado = props.parametros.segmentos.reduce((a, b) => a + b.percentual_ajustado, 0);

  if (Number(total_percentual_configurado) > 100) {
    segmento.percentual_ajustado = 0;
    alert("A soma das proporções não pode ultrapassar 100%.");
  }
}

const totais_produtos_computed = computed(() => {
  return {
    total: props.produtos.reduce((a, b) => {
      const valor_incremento = (props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA')
        ? Number(b.qtd_ajustada)
        : (Number(b.qtd_ajustada) * Number(b.vlr_ajustado));

      a += valor_incremento

      return a;
    }, 0),
    segmentos: props.produtos.reduce((a, b) => {
      const valor_incremento = (props.parametros.tipo_ajuste === 'POR_UNIDADE_MEDIDA')
        ? Number(b.qtd_ajustada)
        : (Number(b.qtd_ajustada) * Number(b.vlr_ajustado));

      a[b.iddepartamento] = (a[b.iddepartamento] || 0) + valor_incremento;

      return a;
    }, {})
  };
});

function removerSegmento({ id }) {
  props.parametros.segmentos = props.parametros.segmentos.filter(item => item.id !== id);
}

function totalPorSegmento(id) {
  return props.produtos
      .filter(item => item.iddepartamento === id)
      .reduce((a, b) => a + (Number(b.qtd_ajustada || 0) * Number(b.vlr_ajustado || 0)), 0);
}
</script>

<style scoped>
#tabela {
  width: 100%;
  border-collapse: collapse;
  border: none;
  margin: 0;
  padding: 0;
}

#tabela tr th, tr td {
  text-align: left;
  color: #013254;
}

#tabela tr th {
  font-size: 12px;
  line-height: 15px;
}

#tabela tr td {
  font-size: 14px;
}

#tabela tr td {
  padding-top: 10px;
}

#tabela tr td div {
  background-color: #F4F4F6;
  height: 45px !important;
  align-items: center;
  display: flex;
}

#tabela tr td:first-child div {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

#tabela tr td:last-child div {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>
